import { isEqual } from 'lodash';
import queryString from 'querystring';
import React, { Component } from 'react';

import analyticService from '../../../../services/analytics';
import { session } from '../../../../services/session';

import { getClass } from '../../../../utils/component-helpers';
import { Loader, NoResult } from '../../../misc/loader-utils';
import searchService from '../search-service';
import Pagination from '../../../base/common/pagination';
import SearchStats from '../search-stats';

class SearchTrends extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.service = searchService;
    this.Session = session;

    this.summaryHeaders = [
      { label: '% Change', key: 'percent_change' },
      { label: 'Searches', key: 'current_count' },
    ];
    this.analyticLabels = {
      percent_change: '%Change',
      current_count: 'Searches',
      search_phrase: 'Search Phrase',
    };
    this.table = null;

    this.state = {
      searches: [],
      loading: false,
      showNoResult: false,

      // order_by: current_count|percent_change|search_phrase
      order_by: 'current_count',
      direction: 'desc',

      // Pagination
      page: 1,
      perPage: this.service.paginationOptions[2],
      totalItems: 0,
    };
  }

  componentDidMount() {
    const updatedState = this.getStateFromQueryString();

    this.setState(updatedState, () => {
      // setQueryString() fills out default values if they aren't set
      this.setQueryString();
      this.querySearchTrends();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const prevMerchIds = queryString.parse(prevProps.urlQuery).merch_ids;
    const currentMerchIds = queryString.parse(this.props.urlQuery).merch_ids;

    if (prevProps.searchTerm !== this.props.searchTerm || !isEqual(prevMerchIds, currentMerchIds)) {
      // Reset query when user selects a different merchant or search
      this.setState(
        {
          searches: [],
          order_by: 'current_count',
          direction: 'desc',
          page: 1,
        },
        () => {
          this.querySearchTrends(true).then(() => {
            // Reset scroll when changing merchant
            this.table.scrollTop = 0;
          });
        },
      );
    }

    /*
     * Display loader when user selects a new merchant to
     * prevent showing debounce delay to clients
     */
    if (!isEqual(prevProps.merchants, this.props.merchants)) {
      this.setState({ loading: true });
    }
  }

  querySearchTrends(resetPages = false) {
    this.setState({
      loading: true,
      showNoResult: false,
      totalItems: 0,
    });
    this.props.updateSearchData({});

    // Make a copy of params because the service nullifies empty search_term
    const params = this.getRequestParams();

    return this.service
      .getSearchTrends(this.props.pubId, params, this.state.page, this.state.perPage, resetPages)
      .then(searches => {
        this.onSearchQuerySuccess(searches, params);
      })
      .catch(() => {
        this.setState({
          loading: false,
          showNoResult: true,
        });
      });
  }

  /* Params handling */
  getStateFromQueryString() {
    // Preserve table state by getting params from the query string
    const searchParams = new URLSearchParams(this.props.location.search);

    return {
      order_by: searchParams.get('order_by') || this.state.order_by,
      direction: searchParams.get('direction') || this.state.direction,
      page: parseInt(searchParams.get('page')) || this.state.page,
      perPage: parseInt(searchParams.get('per_page')) || this.state.perPage,
    };
  }

  getRequestParams() {
    // Return params for the API request
    const { params } = this.props;
    return {
      date_from: params.date_from,
      date_to: params.date_to,
      merch_ids: this.props.merchIds,
      search_term: this.props.searchTerm || null,
      order_by: this.state.order_by,
      direction: this.state.direction,
    };
  }

  setQueryString() {
    /*
     * CartExplorer and SearchTrends are responsible for adding
     * to the query string params that are important to them,
     * using a helper function from Insights.
     */
    const params = this.getRequestParams();
    Object.assign(params, {
      page: this.state.page,
      per_page: this.state.perPage,
    });

    this.props.setQueryString(params);
  }

  /* Actions */
  onSearchQuerySuccess(searches, params) {
    if (!searches.length) {
      this.setState({
        loading: false,
        showNoResult: true,
      });
      return;
    }

    const searchData = this.service.trendsByMerch[params.merch_ids];
    const totalItems = this.service.trendsByMerch[params.merch_ids].total_items;
    this.props.updateSearchData(searchData);

    this.setState({
      searches,
      loading: false,
      showNoResult: false,
      totalItems,
    });

    // Kick off a call to fetch the next page so it will be preloaded
    const nextPage = this.service.limit / this.state.perPage + this.state.page;
    this.service.getSearchTrends(this.props.pubId, params, nextPage, this.state.perPage);
  }

  updateState(propertyName) {
    // Used to update page, per_page
    return value => {
      this.setState({ [propertyName]: value }, () => {
        this.setQueryString();
        this.querySearchTrends().then(() => {
          this.table.scrollTop = 0;
        });
      });
    };
  }

  updateSortParams(columnName) {
    let direction = 'desc';
    // Toggle sort direction if column is the same
    if (this.state.order_by === columnName) {
      direction = this.state.direction === 'desc' ? 'asc' : 'desc';
    }

    const analyticDirection = direction === 'desc' ? 'Descending' : 'Ascending';
    this.AnalyticService.trackEvent(
      'Search Trends',
      'Click',
      `${this.analyticLabels[columnName]} ${analyticDirection}`,
    );

    this.setState(
      prevState => ({
        order_by: columnName,
        direction,
        page: 1,
      }),
      () => {
        this.setQueryString();
        this.querySearchTrends(true);
      },
    );
  }

  render() {
    const trends =
      !this.state.loading && !this.state.showNoResult ? (
        <SearchStats searches={this.state.searches} />
      ) : null;

    const sortIcon = column => {
      return this.state.order_by === column ? (
        <span
          key={`sort-${column}`}
          className={`sort-icon
					${this.state.direction === 'desc' ? 'caret-down' : 'caret-up'}`}
        ></span>
      ) : null;
    };
    const summaryHeaders = this.summaryHeaders.map(column => {
      return (
        <div
          key={`header-${column.key}`}
          className={`column-header ${column.key}`.replace('_', '-')}
          onClick={() => this.updateSortParams(column.key)}
        >
          <span>{column.label}</span>
          {sortIcon(column.key)}
        </div>
      );
    });

    const columnHeaders = (
      <>
        <div
          className="column-header search-phrase"
          onClick={() => this.updateSortParams('search_phrase')}
        >
          <span>Search terms</span>
          {sortIcon('search_phrase')}
        </div>
        <div className="search-summary">{summaryHeaders}</div>
      </>
    );
    const trendsColumnHeader = !this.state.showNoResult ? (
      <div className="trends-column-header">{columnHeaders}</div>
    ) : null;

    return (
      <div className="search-trends">
        <div className="header">
          <h2>Search Trends</h2>
        </div>
        {trendsColumnHeader}
        <div
          ref={node => (this.table = node)}
          className={`search-trends-table ${getClass({
            'no-results': this.state.showNoResult,
            scrollable: this.props.scrollable,
          })}`}
        >
          <Loader loading={this.state.loading} />
          <NoResult
            type="search-trends"
            noResult={!this.state.loading && this.state.showNoResult}
          />
          {trends}
        </div>
        <Pagination
          showPagination={!this.state.showNoResult}
          currentPage={this.state.page}
          totalItems={this.state.totalItems}
          perPage={this.state.perPage}
          eventPrefix="Search Trends"
          updatePage={this.updateState('page')}
          updatePerPage={this.updateState('perPage')}
        />
      </div>
    );
  }
}

export default SearchTrends;
